import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { FormModel, Result } from 'src_common/common/mobx-utils/Form2/FormModel';
import { apiDateToFormDate } from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.helpers';
import { makeAutoObservable, action, computed, reaction } from 'mobx';
import { validateNotEmpty } from 'src_common/common/mobx-utils/Form2/validators';
import {
    isDateAfter,
    isNumericBetween,
    isNumericPrecission,
    isNumericWholeNumber,
    validateIf,
    validateRelativeExpiryDays,
    validatePickerNotEmpty,
    isDateBefore,
} from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.validators';
import { GamePickerState } from 'src/domains/casino/webview/bonus-engine/campaign/action-conditions/casino-sections/CasinoGamesPicker.state';
import { Common } from 'src/AppState/Common';
import { BonusCreationModel } from 'src/domains/casino/state/AppCasino/store/bonus-creation-store/BonusCreationModel';
import { getFieldsFromUsageConditions } from 'src/domains/casino/webview/bonus-engine/bonus-creator/BonusCreator.helpers';

type RewardCriteriaType = 'free-spins';

interface AllRewardCriteriaType {
    value: RewardCriteriaType;
    label: string;
}

const allRewardCriteriaType: Array<AllRewardCriteriaType> = [{ value: 'free-spins', label: 'Free Spins' }];

type CasinoSelectionFormType = {
    expiry: string;
    'relative-expiry-days': string;
    'bet-type': string;
    'number-of-free-spins': string;
    'total-bet': string;
    'play-through': string;
    'free-spin-picker': string;
};

export class CasinoSectionState {
    public readonly expiryForm: FormInputState<string, string>;
    public readonly relativeExpiryDays: FormInputState<string, string>;
    public readonly endDateTimeForm: FormInputState<string, string>;
    public readonly typeForm: FormInputState<string, string>;
    public readonly numberOfFreeSpinsForm: FormInputState<string, string>;
    public readonly totalBetForm: FormInputState<string, string>;
    public readonly playThroughForm: FormInputState<string, string>;
    public readonly installmentCountForm: FormInputState<string, string>;
    public readonly rewardIntervalForm: FormInputState<string, string>;
    public readonly freeSpinsEligibleGamesPickerForm: FormInputState<string, string>;
    public readonly scheduledRewardForm: FormInputState<boolean, boolean>;
    public readonly rewardDateForm: FormInputState<string, string>;

    public readonly freeSpinsEligibleGamesState: GamePickerState;
    public readonly playThroughEligibleGamesState: GamePickerState;

    public readonly form: FormModel<CasinoSelectionFormType>;

    private readonly noEmptyValidationMessage = 'Required field';
    private readonly defaultExpiryType = 'absolute-expiry';

    public constructor(
        public readonly common: Common,
        public readonly bonusCreationId: string | undefined
    ) {
        makeAutoObservable(this);

        this.expiryForm = FormInputState.new(this.defaultExpiryType).map(
            validateNotEmpty(this.noEmptyValidationMessage)
        );
        this.relativeExpiryDays = FormInputState.new('').map(validateRelativeExpiryDays(() => this.expiryForm.value));

        this.typeForm = FormInputState.new('').map(validateNotEmpty(this.noEmptyValidationMessage));
        this.numberOfFreeSpinsForm = FormInputState.new('')
            .map(validateNotEmpty(this.noEmptyValidationMessage))
            .map(isNumericWholeNumber(false, 'Enter a number. Minimum value is 1'));
        this.totalBetForm = FormInputState.new('')
            .map(validateNotEmpty(this.noEmptyValidationMessage))
            .map(isNumericPrecission(2, false, 'Enter a decimal value. Between 0.01 and 10.00'))
            .map(isNumericBetween({ from: 0.01, to: 10 }));
        this.freeSpinsEligibleGamesPickerForm = FormInputState.new('').map(
            validatePickerNotEmpty(this.noEmptyValidationMessage)
        );
        this.playThroughForm = FormInputState.new('')
            .map(validateNotEmpty(this.noEmptyValidationMessage))
            .map(isNumericPrecission(2, false, 'Enter a decimal value. Between 0.01 and 10.00'))
            .map(isNumericBetween({ from: 0, to: 50 }));
        this.endDateTimeForm = FormInputState.new('').map((value) => {
            if (this.expiryForm.value === 'absolute-expiry') {
                return validateNotEmpty(this.noEmptyValidationMessage)(value);
            }
            return Result.createOk(value);
        });

        this.installmentCountForm = FormInputState.new('')
            .map(
                isNumericBetween({
                    from: 1,
                    to: 999,
                    optional: true,
                    message: 'Enter a number between 1 and 999',
                })
            )
            .map(isNumericWholeNumber(true, 'Enter a whole number between 1 and 999'))
            .map((value) => {
                return validateIf(
                    () => this.rewardIntervalForm.value.length > 0,
                    () => validateNotEmpty('Required field if reward interval has been set')(value)
                );
            });
        this.rewardIntervalForm = FormInputState.new('')
            .map(
                isNumericBetween({
                    from: 1,
                    to: 999,
                    optional: true,
                    message: 'Enter a number between 1 and 999',
                })
            )
            .map(isNumericWholeNumber(true, 'Enter a whole number between 1 and 999'))
            .map((value) => {
                return validateIf(
                    () => this.installmentCountForm.value.length > 0,
                    () => validateNotEmpty('Required field if installment count has been set')(value)
                );
            });

        this.freeSpinsEligibleGamesState = new GamePickerState(
            $appState.appCasinoState.gameStore,
            'casino',
            this.freeSpinsEligibleGamesPickerForm
        );
        this.playThroughEligibleGamesState = new GamePickerState($appState.appCasinoState.gameStore, 'casino');

        this.scheduledRewardForm = FormInputState.new(false);
        this.rewardDateForm = FormInputState.new('')
            .map((value) => {
                return validateIf(
                    () => this.scheduledReward,
                    () => validateNotEmpty(this.noEmptyValidationMessage)(value)
                );
            })
            .map(isDateAfter(() => new Date().toString(), false, "Reward date can't be in the past"))
            .map((value) => {
                return validateIf(
                    () => this.scheduledReward,
                    () => {
                        if (this.expiryForm.value !== 'absolute-expiry') {
                            return Result.createOk(value);
                        }

                        return isDateBefore(
                            () => this.endDateTimeForm.value,
                            false,
                            'The Reward date must be before the Expiration date.'
                        )(value);
                    }
                );
            });

        this.form = FormModel.group({
            expiry: this.expiryForm,
            'relative-expiry-days': this.relativeExpiryDays,
            'bet-type': this.typeForm,
            'number-of-free-spins': this.numberOfFreeSpinsForm,
            'total-bet': this.totalBetForm,
            'play-through': this.playThroughForm,
            'installment-count': this.installmentCountForm,
            'reward-interval': this.rewardIntervalForm,
            'end-date-time': this.endDateTimeForm,
            'free-spin-picker': this.freeSpinsEligibleGamesPickerForm,
            'scheduled-reward': this.scheduledRewardForm,
            'reward-date': this.rewardDateForm,
        });

        reaction(
            () => this.expiryForm.value,
            (value) => {
                if (value === 'absolute-expiry') {
                    this.relativeExpiryDays.setValue('');
                } else if (value === 'relative-expiry') {
                    this.endDateTimeForm.setValue('');
                }
            }
        );
    }

    @computed public get scheduledReward(): boolean {
        return this.scheduledRewardForm.value;
    }

    @action public validateForm(): void {
        this.form.setAsVisited();
    }

    @computed public get isFormValid(): boolean {
        return this.form.result.value.type === 'ok';
    }

    @computed public get formErrorsCount(): number {
        return this.form.errorsForView.length;
    }

    public isTouched(): boolean {
        return (
            this.form.isTouched() ||
            this.freeSpinsEligibleGamesState.isTouched() ||
            this.playThroughEligibleGamesState.isTouched() ||
            this.freeSpinsEligibleGamesPickerForm.isTouched()
        );
    }

    public setIsTouched(): void {
        this.form.setIsTouched();
    }

    public resetIsTouched(): void {
        this.form.resetIsTouched();
        this.freeSpinsEligibleGamesState.resetIsTouched();
        this.playThroughEligibleGamesState.resetIsTouched();
        this.freeSpinsEligibleGamesPickerForm.resetIsTouched();
    }

    public resetIsVisited(): void {
        this.form.resetIsVisited();
    }

    public get allRewardCriteriaOptions(): Array<AllRewardCriteriaType> {
        return allRewardCriteriaType;
    }

    @action public setFormValues = (bonusRewardModel: BonusCreationModel): void => {
        const rewardType = bonusRewardModel.rewardType;

        if (rewardType === 'free-spin') {
            const expiryDate = apiDateToFormDate(bonusRewardModel.rewardExpiryDateApi);
            this.endDateTimeForm.setValue(expiryDate);

            const relativeExpiryDays = bonusRewardModel.relativeExpiryDays;
            this.relativeExpiryDays.setValue(relativeExpiryDays === null ? '' : relativeExpiryDays.toString());

            const hasRelativeExpiry = relativeExpiryDays !== null;
            this.expiryForm.setValue(hasRelativeExpiry ? 'relative-expiry' : this.defaultExpiryType);

            this.installmentCountForm.setValue(bonusRewardModel.installmentCount?.toString() ?? '');
            this.rewardIntervalForm.setValue(bonusRewardModel.rewardInterval?.toString() ?? '');
        }

        const transformedCriteria = getFieldsFromUsageConditions(bonusRewardModel);
        this.typeForm.setValue(transformedCriteria?.['bet-type'] ?? 'free-spins'); // TODO: not returning valid data
        this.numberOfFreeSpinsForm.setValue(String(bonusRewardModel.rewardAmount?.value ?? ''));
        this.totalBetForm.setValue(String(bonusRewardModel.totalBet ?? ''));
        this.playThroughForm.setValue(String(bonusRewardModel.playthrough ?? ''));

        this.scheduledRewardForm.setValue(bonusRewardModel.scheduledReward ?? false);
        const rewardDate = apiDateToFormDate(bonusRewardModel.rewardDate);
        this.rewardDateForm.setValue(rewardDate);

        const playthroughEligibleGames = bonusRewardModel.playthroughEligibleGames;

        if (Array.isArray(playthroughEligibleGames)) {
            this.playThroughEligibleGamesState.setChecked(playthroughEligibleGames);
        }

        const usageConditions = bonusRewardModel.usageConditions?.[0];

        if (usageConditions?.type === 'casino-game') {
            if (Array.isArray(usageConditions.value)) {
                this.freeSpinsEligibleGamesState.setChecked(usageConditions.value);
            }
        } else if (this.freeSpinsEligibleGamesState.isEmpty() === false) {
            this.freeSpinsEligibleGamesState.clearChecked();
        }
    };
}
